import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';

import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SharedModule } from 'primeng/api';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';

@Component({
  selector: 'app-dtp-edit-modal',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    TranslateModule,
    Dialog,
    SharedModule,
    InputText,
    TextareaModule,
    AppButton
  ],
  templateUrl: './dtp-edit-modal.html',
})
export class DtpEditModal implements OnInit {
  @Input() type: 'diagnostic' | 'treatment' = 'diagnostic';
  @Input() item: any = null;
  @Input() parentId: number | null = null;
  @Input() branchId!: number;
  /** AI provider entries that import as this diagnosis. Deleting it clears
   *  those links, so the confirmation has to say what stops working. */
  @Input() linkedProviderNames: string[] = [];
  
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);

  form!: FormGroup;
  isEditMode = false;
  saving = false;
  showDeleteConfirm = false;

  get titleKey() {
    if (this.showDeleteConfirm) return 'confirm_delete';
    const prefix = this.isEditMode ? 'edit_label' : 'add_label';
    const typeLabel = this.type === 'diagnostic' ? 'diagnostics_label' : 'treatments_label';
    return `${prefix}`; // Simplified for now using standard keys
  }

  get typeLabel() {
    return this.translate.instant(this.type === 'diagnostic' ? 'diagnostics_label' : 'treatments_label');
  }

  ngOnInit() {
    this.isEditMode = !!this.item;
    this.initForm();
    if (this.isEditMode) {
      this.form.patchValue({
        name: this.item.name,
        description: this.item.description || ''
      });
    }
  }

  initForm() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });
  }

  async save() {
    if (this.form.invalid) return;
    this.saving = true;
    try {
      const { name, description } = this.form.value;
      if (this.type === 'diagnostic') {
        if (this.isEditMode) {
          await firstValueFrom(this.settingsService.updateDiagnostic((this.item as any).id, name, description));
        } else {
          await firstValueFrom(this.settingsService.createDiagnostic(name, description, this.branchId));
        }
      } else if (this.type === 'treatment') {
        if (this.isEditMode) {
          await firstValueFrom(this.settingsService.updateTreatment((this.item as any).id, name, description));
        } else {
          await firstValueFrom(this.settingsService.createTreatment(name, description, this.parentId!, this.branchId));
        }
      }
      toast.success(this.translate.instant('save_success'));
      this.saved.emit();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_saving_data'));
    } finally {
      this.saving = false;
    }
  }

  async delete() {
    this.saving = true;
    try {
      if (this.type === 'diagnostic') {
        await firstValueFrom(this.settingsService.deleteDiagnostic((this.item as any).id));
      } else {
        await firstValueFrom(this.settingsService.deleteTreatment((this.item as any).id));
      }
      toast.success(this.translate.instant('delete_success'));
      this.deleted.emit();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_deleting_data'));
    } finally {
      this.saving = false;
      this.showDeleteConfirm = false;
    }
  }
}
